import {FC, Key, ReactNode, useCallback} from 'react';
import {css, styled} from 'styled-components';

import {UnthemedNavLink} from '@shared_web/components/core/button';
import {SvgIcon} from '@shared_web/components/core/svg_icon';
import {Flex} from '@shared_web/components/core2/flex';

import {icon_chevron_right} from '@mavismart_frontend/icons/icon_chevron_right';
import {
  LIST_CELL_DEFAULT_COLOR,
  LIST_CELL_HIGHLIGHTED_COLOR,
  LIST_CELL_HOVER_COLOR,
  TEXT_MAIN_COLOR,
} from '@mavismart_frontend/lib/constants';

interface GenericCellProps {
  id: Key;
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  to?: string;
  onClick?: (id: Key) => void;
  rightIcon?: ReactNode;
  status?: 'unread' | 'read';
  highlighted?: boolean;
}

export const GenericCell: FC<GenericCellProps> = ({
  id,
  title,
  subtitle,
  icon,
  to,
  onClick,
  rightIcon,
  status,
  highlighted,
}) => {
  const content = (
    <>
      {icon && <LeftIcon>{icon}</LeftIcon>}
      <GenericInfo>
        <Title>{title}</Title>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </GenericInfo>
      {status ? (
        status === 'unread' ? (
          <Flex $align="center" $justify="center" $shrink>
            {rightIcon}
          </Flex>
        ) : (
          <Flex />
        )
      ) : (
        <Flex $align="center" $justify="center" $shrink>
          {rightIcon ? (
            rightIcon
          ) : (
            <SvgIcon icon={icon_chevron_right} color="transparent" size={16} />
          )}
        </Flex>
      )}
    </>
  );

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick(id);
    }
  }, [onClick, id]);

  const isClickable = to !== undefined || onClick !== undefined;

  return to ? (
    <WrapperGeneric
      to={to}
      $isClickable={isClickable}
      $highlighted={highlighted}
      onClick={handleClick}
    >
      {content}
    </WrapperGeneric>
  ) : (
    <WrapperParams
      as="div"
      onClick={handleClick}
      $isClickable={isClickable}
      $highlighted={highlighted}
    >
      {content}
    </WrapperParams>
  );
};

GenericCell.displayName = 'GenericCell';

const LeftIcon = styled.div`
  width: 24px;
  margin-top: 6px;
`;

const Title = styled.div`
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Subtitle = styled.div`
  white-space: normal;
  word-wrap: break-word; 
  color: ${TEXT_MAIN_COLOR}aa;
`;

const WrapperCss = css<{$isClickable?: boolean; $highlighted?: boolean}>`  
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 10px;
  border-bottom: 1px solid #eceaea;
  &:first-of-type {
    border-top: 1px solid #eceaea;
  }
  cursor: ${props => (props.$isClickable ? 'pointer' : 'default')};
  padding-left: 20px;
  padding-right: 20px;
  background-color: ${props => (props.$highlighted ? LIST_CELL_HIGHLIGHTED_COLOR : LIST_CELL_DEFAULT_COLOR)};
  &:hover {
    background-color: ${props => (props.$highlighted ? LIST_CELL_HIGHLIGHTED_COLOR : props.$isClickable ? LIST_CELL_HOVER_COLOR : LIST_CELL_DEFAULT_COLOR)};
  }
`;

const WrapperGeneric = styled(UnthemedNavLink)<{$isClickable?: boolean; $highlighted?: boolean}>`
  ${WrapperCss}
`;

const WrapperParams = styled.div<{$isClickable?: boolean; $highlighted?: boolean}>`
  ${WrapperCss}
`;

const GenericInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 0;
`;
